// components/child/PivotViewStore.jsx

import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Save,
  FolderOpen,
  Trash2,
  Edit2,
  Search,
  Clock,
  Database,
  Loader2,
  Globe,
  User2,
  LockIcon,
  Table,
  Calculator,
} from "lucide-react";
import { apiCall } from "@/api/apiCall";
import { Switch } from "../ui/switch";
import { Label } from "../ui/label";
import { formatDateTime } from "@/lib/formatDateTime";

// Cube identity = server + value, so same-named cubes on different servers stay distinct
const cubeKey = (server, value) => `${server ?? ""}::${value ?? ""}`;

const getCurrentUser = () => {
  try {
    return JSON.parse(localStorage.getItem("user")) || {};
  } catch {
    return {};
  }
};

const isGlobalView = (view) => view?.global_view === "Y" || view?.global_view === true;

const VIEW_FILTERS = [
  { key: "all", label: "All Views" },
  { key: "my", label: "My Views" },
  { key: "global", label: "Global Views" },
];

const PivotViewStore = ({
  currentViewData,
  selectedCube,
  availableDimensions,
  onCubeChange,
  onApplyDimensions,
  currentLoadedViewId,
  currentLoadedViewName,
  onViewLoaded,
  onViewNameLoaded,
  mode = "full",
  isSaveEnabled = true,
  // Cubes the user is authorized for ({ value, server }[]). When omitted, every view is accessible.
  accessibleCubes,
}) => {
  const [savedViews, setSavedViews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingAsNew, setSavingAsNew] = useState(false);
  const [updatingView, setUpdatingView] = useState(false);
  const [isMainDialogOpen, setIsMainDialogOpen] = useState(false);
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState("list");
  const [viewName, setViewName] = useState("");
  const [newViewName, setNewViewName] = useState(""); // ← New: optional name for new view
  const [editingView, setEditingView] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [pendingView, setPendingView] = useState(null);
  const [viewStatus, setViewStatus] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [viewToDelete, setViewToDelete] = useState(null);
  const [viewFilter, setViewFilter] = useState("all");

  // Owner check — prefer user id, fall back to name when the API doesn't send an id
  const isOwnView = useCallback((view) => {
    const user = getCurrentUser();
    if (view?.saved_by_id != null && user?.user_id != null) {
      return String(view.saved_by_id) === String(user.user_id);
    }
    const userName = (user?.name || "").trim().toLowerCase();
    return !!userName && (view?.saved_by || "").trim().toLowerCase() === userName;
  }, []);

  const accessibleCubeKeys = useMemo(
    () =>
      Array.isArray(accessibleCubes)
        ? new Set(accessibleCubes.map((c) => cubeKey(c?.server, c?.value)))
        : null,
    [accessibleCubes]
  );

  const hasCubeAccess = useCallback(
    (view) =>
      !accessibleCubeKeys || accessibleCubeKeys.has(cubeKey(view?.cube?.server, view?.cube?.value)),
    [accessibleCubeKeys]
  );

  // Fetch all saved views
  const fetchSavedViews = async () => {
    setLoading(true);
    try {
      const response = await apiCall("get", "/get-all-pivot-views");
      if (response?.success && Array.isArray(response.data)) {
        const normalizedViews = response.data.map((item) => ({
          id: item.id.toString(),
          name: item.view_name,
          cube: {
            label: item.cube.value,
            value: item.cube.value,
            server: item.cube.server,
          },
          rowDimensions: item.rowDimensions || [],
          colDimensions: item.colDimensions || [],
          selectedElements: item.selectedElements || {},
          calculatedColumns: item.calculatedColumns || [],
          global_view: item?.global_view || false,
          saved_by: item?.saved_by?.name || "Unknown",
          saved_by_id: item?.saved_by?.id ?? item?.saved_by?.user_id ?? item?.user_id ?? null,
          savedAt: item.created_at,
        }));
        setSavedViews(normalizedViews);
      } else {
        setSavedViews([]);
      }
    } catch (error) {
      console.error("Failed to fetch saved views:", error);
      setSavedViews([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isMainDialogOpen) {
      fetchSavedViews();
    }
  }, [isMainDialogOpen]);

  useEffect(() => {
    if (pendingView && availableDimensions.length > 0) {
      const timer = setTimeout(() => {
        if (!hasCubeAccess(pendingView)) {
          setPendingView(null);
          return;
        }
        onApplyDimensions(pendingView);
        onViewLoaded?.(pendingView.id);
        onViewNameLoaded?.(pendingView.name);
        setPendingView(null);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [availableDimensions, pendingView]);

  // Views for cubes the user cannot access are never shown
  const accessibleViews = useMemo(
    () => savedViews.filter(hasCubeAccess),
    [savedViews, hasCubeAccess]
  );

  const matchesFilter = (view, filterKey) =>
    filterKey === "my" ? isOwnView(view) : filterKey === "global" ? isGlobalView(view) && !isOwnView(view) : true;

  const searchedViews = accessibleViews.filter(
    (view) =>
      view.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (view.cube?.value || "").toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredViews = searchedViews.filter((view) => matchesFilter(view, viewFilter));

  // Per-tab counts follow the search term so they match what each tab would show
  const filterCounts = VIEW_FILTERS.reduce((acc, f) => {
    acc[f.key] = searchedViews.filter((view) => matchesFilter(view, f.key)).length;
    return acc;
  }, {});

  // Manage Views always starts on the list with "All" selected — never a stale Rename screen
  const handleMainDialogOpenChange = (open) => {
    setDialogMode("list");
    setEditingView(null);
    setViewName("");
    if (open) setViewFilter("all");
    setIsMainDialogOpen(open);
  };

  // Save as New View
  const performSaveAsNew = async () => {
    if (!currentViewData?.cube) {
      toast.error("Please select a cube first");
      return;
    }

    const payload = {
      cube: {
        value: currentViewData.cube.value,
        server: currentViewData.cube.server,
      },
      rowDimensions: [...currentViewData.rowDimensions],
      colDimensions: [...currentViewData.colDimensions],
      selectedElements: JSON.parse(JSON.stringify(currentViewData.selectedElements)),
      calculatedColumns: currentViewData.calculatedColumns || [],
      view_name: newViewName.trim() || null, // optional — null bhejo agar empty hai
    };

    try {
      setSavingAsNew(true);
      const response = await apiCall("post", "/create-pivot-view", payload);

      if (response?.success) {
        await fetchSavedViews();
        onViewLoaded?.(null);
        onViewNameLoaded?.(null);
        setIsConfirmDialogOpen(false);
        setNewViewName(""); // reset
      } else {
        toast.error(response?.message || "Failed to save view");
      }
    } catch (error) {
      console.error("Something went wrong while saving", error);
      toast.error("Failed to save view");
    } finally {
      setSavingAsNew(false);
    }
  };

  // Update existing view
  const performUpdateView = async () => {
    if (!currentViewData?.cube) {
      toast.error("Please select a cube first");
      return;
    }

    const payload = {
      id: currentLoadedViewId,
      view_name: currentLoadedViewName,
      cube: {
        value: currentViewData.cube.value,
        server: currentViewData.cube.server,
      },
      rowDimensions: [...currentViewData.rowDimensions],
      colDimensions: [...currentViewData.colDimensions],
      selectedElements: JSON.parse(JSON.stringify(currentViewData.selectedElements)),
      calculatedColumns: currentViewData.calculatedColumns || [],
    };

    try {
      setUpdatingView(true);
      const response = await apiCall("post", "/create-pivot-view", payload);

      if (response?.success) {
        await fetchSavedViews();
        setIsConfirmDialogOpen(false);
      } else {
        toast.error(response?.message || "Failed to update view");
      }
    } catch (error) {
      console.error("Something went wrong while updating", error);
      toast.error("Failed to update view");
    } finally {
      setUpdatingView(false);
    }
  };

  const handleRename = async () => {
    if (!editingView || !viewName.trim()) {
      toast.error("Please enter a valid name");
      return;
    }
    if (!hasCubeAccess(editingView) || !isOwnView(editingView)) {
      toast.error("You can only rename your own views");
      return;
    }

    try {
      setLoading(true);
      const response = await apiCall("put", `/update-view-name/${editingView.id}`, {
        view_name: viewName.trim(),
        global_view: viewStatus ? true : false,
      });

      if (response?.success) {
        setViewName("");
        setEditingView(null);
        setDialogMode("list");
        await fetchSavedViews();
      }
    } catch (error) {
      console.error("Failed to rename view");
      toast.error("Failed to rename view");
    } finally {
      setLoading(false);
    }
  };

  const handleLoadView = (view) => {
    if (!view?.cube) {
      toast.error("Invalid view");
      return;
    }
    if (!hasCubeAccess(view)) {
      toast.error("You do not have access to this view's cube");
      return;
    }

    const isSameCube =
      selectedCube &&
      view.cube.value === selectedCube.value &&
      view.cube.server === selectedCube.server;

    const viewToLoad = {
      ...view,
      calculatedColumns: view.calculatedColumns || [],
    };

    if (isSameCube && availableDimensions.length > 0) {
      onApplyDimensions(viewToLoad);
      onViewLoaded?.(view.id);
      onViewNameLoaded?.(view.name);
      setIsMainDialogOpen(false);
    } else {
      setPendingView({ ...viewToLoad });
      onCubeChange(view.cube);
      setIsMainDialogOpen(false);
    }
  };

  const handleDeleteClick = (view, e) => {
    e?.stopPropagation();
    if (!hasCubeAccess(view) || !isOwnView(view)) return;
    setViewToDelete(view);
    setIsDeleteConfirmOpen(true);
  };

  const handleDelete = async () => {
    if (!viewToDelete || !hasCubeAccess(viewToDelete) || !isOwnView(viewToDelete)) return;
    try {
      setLoading(true);
      const response = await apiCall("delete", `/delete-pivot-view/${viewToDelete.id}`);
      if (response?.success) {
        await fetchSavedViews();
        if (currentLoadedViewId === viewToDelete.id) {
          onViewLoaded?.(null);
          onViewNameLoaded?.(null);
        }
      }
    } catch (error) {
      console.error("Failed to delete view", error);
      toast.error("Failed to delete view");
    } finally {
      setLoading(false);
      setIsDeleteConfirmOpen(false);
      setViewToDelete(null);
    }
  };

  const handleEditClick = (view, e) => {
    e?.stopPropagation();
    if (!hasCubeAccess(view) || !isOwnView(view)) return;
    setEditingView(view);
    setViewName(view.name);
    setViewStatus(view.global_view === "Y");
    setDialogMode("edit");
  };

  const hasLoadedView = !!currentLoadedViewId;

  // Dialog open karte waqt newViewName reset karo
  const openSaveDialog = () => {
    setNewViewName("");
    setIsConfirmDialogOpen(true);
  };

  return (
    <>
      {/* Trigger Buttons */}
      {mode === "save-only" ? (
        <Button
          onClick={openSaveDialog}
          disabled={!isSaveEnabled || loading}
          size="sm"
          variant="default"
          className="flex items-center gap-2"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Save View
        </Button>
      ) : (
        <Button
          onClick={() => handleMainDialogOpenChange(true)}
          variant="outline"
          size="sm"
          disabled={loading}
          className="flex items-center gap-2 h-7 px-2.5 text-xs"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <FolderOpen className="w-4 h-4" />}
          <span className="hidden sm:inline">Manage Views</span>
          <span className="sm:hidden">Views</span>
          {accessibleViews.length > 0 && (
            <span className="ml-1 px-1 py-0 text-[10px] font-semibold bg-primary/10 text-primary rounded-full">
              {accessibleViews.length}
            </span>
          )}
        </Button>
      )}

      {/* Main Dialog - Only in full mode */}
      {mode === "full" && (
        <Dialog open={isMainDialogOpen} onOpenChange={handleMainDialogOpenChange}>
          <DialogContent className="max-w-3xl w-full max-h-[90vh] p-0 flex flex-col overflow-hidden">
            <DialogHeader className="px-6 py-5 border-b shrink-0">
              <div className="flex items-end justify-between gap-4">
                <div>
                  <DialogTitle className="text-xl font-bold">
                    {dialogMode === "list" ? "Pivot Views" : "Rename View"}
                  </DialogTitle>
                  {dialogMode === "list" && (
                    <p className="text-sm text-muted-foreground mt-1">
                      Manage your saved pivot configurations
                    </p>
                  )}
                </div>
                {/* {dialogMode === "list" && isSaveEnabled && (
                  <Button onClick={openSaveDialog} size="sm" disabled={loading}>
                    <Save className="w-4 h-4 mr-2" />
                    Save View
                  </Button>
                )} */}
              </div>
            </DialogHeader>

            <div className="flex-auto min-h-0 overflow-hidden flex flex-col">
              {dialogMode === "list" && (
                <>
                  {accessibleViews.length > 0 && (
                    <div className="px-6 py-3 border-b shrink-0">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground w-4 h-4" />
                        <Input
                          placeholder="Search views..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className="pl-10 h-10"
                        />
                      </div>
                      <div className="flex gap-1 mt-3 p-1 bg-muted rounded-lg w-fit">
                        {VIEW_FILTERS.map((f) => (
                          <button
                            key={f.key}
                            type="button"
                            onClick={() => setViewFilter(f.key)}
                            className={`flex items-center gap-1.5 px-4 py-1.5 text-xs font-medium rounded-md transition-colors ${
                              viewFilter === f.key
                                ? "bg-background text-foreground shadow-sm"
                                : "text-muted-foreground hover:text-foreground"
                            }`}
                          >
                            {f.label}
                            <span
                              className={`min-w-[1.25rem] px-1.5 py-0.5 rounded-full text-[10px] leading-none text-center ${
                                viewFilter === f.key
                                  ? "bg-primary text-primary-foreground"
                                  : "bg-muted-foreground/15 text-muted-foreground"
                              }`}
                            >
                              {filterCounts[f.key]}
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="flex-auto min-h-0 overflow-y-auto px-6 py-4">
                    {loading && accessibleViews.length === 0 ? (
                      <div className="flex items-center justify-center h-64">
                        <Loader2 className="w-8 h-8 animate-spin text-primary" />
                      </div>
                    ) : accessibleViews.length === 0 ? (
                      <div className="flex flex-col items-center justify-center h-full text-center py-12">
                        <div className="w-20 h-20 rounded-full bg-slate-100 flex items-center justify-center mb-4">
                          <FolderOpen className="w-10 h-10 text-slate-400" />
                        </div>
                        <h3 className="text-lg font-semibold mb-2">No Saved Views</h3>
                        <p className="text-sm text-muted-foreground mb-6">
                          Save your current configuration to get started
                        </p>
                        <Button onClick={openSaveDialog} disabled={!isSaveEnabled}>
                          <Save className="w-4 h-4 mr-2" /> Save Current View
                        </Button>
                      </div>
                    ) : filteredViews.length === 0 ? (
                      <div className="text-center py-12 text-muted-foreground">
                        <Search className="w-10 h-10 mx-auto mb-4 text-slate-300" />
                        <p>No matching views found</p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 gap-2.5">
                        {filteredViews.map((view) => {
                          const isCurrent = currentLoadedViewId === view.id;
                          const isGlobal = view.global_view === "Y";
                          const calcCount = view.calculatedColumns?.length || 0;

                          return (
                            <div
                              key={view.id}
                              role="button"
                              tabIndex={0}
                              onClick={() => handleLoadView(view)}
                              onKeyDown={(e) => {
                                // Only the card itself — Enter/Space on Edit/Delete must not load the view
                                if (e.target !== e.currentTarget) return;
                                if (e.key === "Enter" || e.key === " ") {
                                  e.preventDefault();
                                  handleLoadView(view);
                                }
                              }}
                              className={`group relative flex min-w-0 items-start gap-3 rounded-xl border bg-card px-4 py-3 shadow-xs transition-all cursor-pointer outline-none hover:border-primary/40 hover:shadow-sm focus-visible:border-primary/50 focus-visible:ring-2 focus-visible:ring-ring/40 ${
                                isCurrent ? "border-primary/50 bg-primary/3" : "border-border"
                              }`}
                            >
                              {/* Leading icon */}
                              {/* <div
                                className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg transition-colors ${
                                  isCurrent
                                    ? "bg-primary text-primary-foreground"
                                    : "bg-primary/10 text-primary group-hover:bg-primary/15"
                                }`}
                              >
                                <Table className="h-4 w-4" />
                              </div> */}

                              {/* Content */}
                              <div className="min-w-0 flex-1">
                                <div className="flex min-w-0 items-center gap-2">
                                  <span
                                    className="truncate text-sm font-semibold text-foreground"
                                    title={view.name}
                                  >
                                    {view.name}
                                  </span>
                                  {isCurrent && (
                                    <span className="shrink-0 rounded-full bg-primary px-1.5 py-0.5 text-[10px] font-medium leading-none text-primary-foreground">
                                      Current
                                    </span>
                                  )}
                                </div>

                                <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                                  <span
                                    className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium ${
                                      isGlobal
                                        ? "bg-blue-500/10 text-blue-700 dark:text-blue-400"
                                        : "bg-muted text-muted-foreground"
                                    }`}
                                  >
                                    {isGlobal ? (
                                      <Globe className="h-3 w-3" />
                                    ) : (
                                      <LockIcon className="h-3 w-3" />
                                    )}
                                    {isGlobal ? "Global" : "Private"}
                                  </span>
                                  {calcCount > 0 && (
                                    <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/10 px-2 py-0.5 text-[10px] font-medium text-amber-700 dark:text-amber-400">
                                      <Calculator className="h-3 w-3" />
                                      {calcCount} calc column{calcCount !== 1 ? "s" : ""}
                                    </span>
                                  )}
                                </div>

                                <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                                  <span className="inline-flex min-w-0 max-w-full items-center gap-1.5">
                                    <Database className="h-3.5 w-3.5 shrink-0" />
                                    <span className="truncate" title={view.cube.value}>
                                      {view.cube.value}
                                    </span>
                                  </span>
                                  <span className="inline-flex items-center gap-1.5">
                                    <User2 className="h-3.5 w-3.5 shrink-0" />
                                    {view.saved_by}
                                  </span>
                                  <span className="inline-flex items-center gap-1.5">
                                    <Clock className="h-3.5 w-3.5 shrink-0" />
                                    {formatDateTime(view.savedAt)}
                                  </span>
                                </div>
                              </div>

                              {/* Owner actions — always visible on every card the user can edit/delete */}
                              {isOwnView(view) && (
                                <div className="flex shrink-0 items-center gap-0.5">
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8 text-muted-foreground hover:text-foreground"
                                    title="Rename view"
                                    aria-label={`Rename ${view.name}`}
                                    onClick={(e) => handleEditClick(view, e)}
                                  >
                                    <Edit2 className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                                    title="Delete view"
                                    aria-label={`Delete ${view.name}`}
                                    onClick={(e) => handleDeleteClick(view, e)}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </>
              )}

              {/* Rename Mode */}
              {dialogMode === "edit" && editingView && (
                <div className="p-6 space-y-6">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">
                      New Name <span className="text-red-500">*</span>
                    </label>
                    <Input
                      value={viewName}
                      onChange={(e) => setViewName(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && viewName.trim() && handleRename()}
                      placeholder="Enter new view name"
                      autoFocus
                    />
                  </div>
                  <div className="flex items-center space-x-3">
                    <Switch
                      checked={viewStatus}
                      onCheckedChange={setViewStatus}
                      id="global-view-status"
                    />
                    <Label htmlFor="global-view-status" className="text-sm font-medium cursor-pointer">
                      Global View {viewStatus ? "(Visible to all)" : "(Private)"}
                    </Label>
                  </div>
                  <DialogFooter className="flex gap-3 pt-4 border-t">
                    <Button variant="outline" onClick={() => setDialogMode("list")}>
                      Cancel
                    </Button>
                    <Button onClick={handleRename} disabled={!viewName.trim() || loading}>
                      {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                      Update Name
                    </Button>
                  </DialogFooter>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* ✅ Save Confirmation Dialog — ab input field bhi hai andar */}
      <Dialog open={isConfirmDialogOpen} onOpenChange={setIsConfirmDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Save Current View</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <p className="text-sm text-muted-foreground">
              How would you like to save this configuration?
            </p>

            {/* ✅ Yeh naya input field hai — optional name for new view */}
            <div className="space-y-1">
              <label className="text-sm font-medium">
                View Name{" "}
                <span className="text-xs text-muted-foreground font-normal">(optional)</span>
              </label>
              <Input
                value={newViewName}
                onChange={(e) => setNewViewName(e.target.value)}
                placeholder="e.g. Monthly Sales Report"
                onKeyDown={(e) => e.key === "Enter" && performSaveAsNew()}
              />
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                variant="outline"
                onClick={performSaveAsNew}
                disabled={savingAsNew || updatingView}
                className="flex-1"
              >
                {savingAsNew ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                Save as New View
              </Button>

              {hasLoadedView && (
                <Button
                  onClick={performUpdateView}
                  disabled={updatingView || savingAsNew}
                  className="flex-1"
                >
                  {updatingView ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Update Current View
                </Button>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsConfirmDialogOpen(false)}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteConfirmOpen} onOpenChange={setIsDeleteConfirmOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Delete View</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-muted-foreground">
              Are you sure you want to delete{" "}
              <span className="font-semibold text-foreground">"{viewToDelete?.name}"</span>? This
              action cannot be undone.
            </p>
          </div>
          <DialogFooter className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setIsDeleteConfirmOpen(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={loading}>
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Trash2 className="w-4 h-4 mr-2" />
              )}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PivotViewStore;